import React from 'react';
import { useInView } from '../hooks/useInView';

const datasheetHighlights = [
  'Review-by-exception batch release',
  '21 CFR Part 11 audit trail and e-signatures',
  'Configurable MBR, workflow, roles, limits, and gates',
  'Veeva Vault connector and ERP disposition queue',
];

const specs = [
  { label: 'Deployment', value: 'Cloud VPC, private cloud, or customer-managed Kubernetes' },
  { label: 'Security', value: 'SSO/SAML, RBAC, MFA-ready, site-scoped permissions, encrypted data' },
  { label: 'Compliance', value: '21 CFR Part 11, EU Annex 11/16, GAMP 5 validation support' },
  { label: 'Data Model', value: 'MBR/BPR versioning, exception objects, audit events, batch genealogy' },
  { label: 'Integrations', value: 'REST APIs, webhooks, connector SDK, Veeva, ERP, MES, LIMS, historians' },
  { label: 'AI Controls', value: 'Human verification, read-only assistant, validated-build LLM disable switch' },
];

const implementationSteps = [
  { week: 'Weeks 1-2', title: 'Discovery & fit-gap', body: 'Map current BPR review, release gates, SOPs, roles, products, and critical process parameters.' },
  { week: 'Weeks 3-5', title: 'Configuration & migration', body: 'Configure workflows, limits, e-sign rules, MBR templates, exception taxonomy, and legacy record intake.' },
  { week: 'Weeks 6-8', title: 'Integration & validation pack', body: 'Connect identity, Veeva/QMS, ERP queue, reporting exports, and complete IQ/OQ/PQ evidence support.' },
  { week: 'Weeks 9-10', title: 'Pilot release', body: 'Run guided batch review, close deviations, train QA/operators, and promote the validated configuration.' },
];

const modulePricing = [
  { module: 'Batch & Manufacturing Quality', inr: '₹3,800', usd: '$40' },
  { module: 'iQMS Modules', inr: '₹3,800', usd: '$40' },
  { module: 'Change Control & Management', inr: '₹2,400', usd: '$25' },
  { module: 'Risk, Compliance & Audit Management', inr: '₹1,900', usd: '$20' },
  { module: 'Documentation & Training', inr: '₹1,900', usd: '$20' },
  { module: 'LIMS', inr: '₹3,800', usd: '$40' },
  { module: 'QC & Laboratory Quality', inr: '₹3,800', usd: '$40' },
  { module: 'Supplier & External Quality', inr: '₹3,800', usd: '$40' },
  { module: 'Equipment, Facility & Operations Quality', inr: '₹3,800', usd: '$40' },
  { module: 'Market / Customer Quality', inr: '₹1,900', usd: '$20' },
  { module: 'Quality Intelligence & Analytics', inr: '₹3,800', usd: '$40' },
];

function SectionTitle({ eyebrow, title, body, color = '#00C9B1' }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <p style={{
        fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
        color, marginBottom: 12, fontFamily: "'JetBrains Mono', monospace",
      }}>{eyebrow}</p>
      <h2 style={{
        fontSize: 'clamp(28px, 4.2vw, 48px)', fontWeight: 900,
        lineHeight: 1.08, letterSpacing: '-0.03em', color: '#fff', marginBottom: 14,
      }}>{title}</h2>
      <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.52)', maxWidth: 640, lineHeight: 1.7 }}>{body}</p>
    </div>
  );
}

function ArchitectureDiagram() {
  const layers = [
    { name: 'Users', items: ['QA', 'Operators', 'Supervisors', 'QP', 'IT'], color: '#00C9B1' },
    { name: 'Qualicore Apps', items: ['MBR Authoring', 'Batch Review', 'Exceptions', 'BI', 'AI Assistant'], color: '#7B3FE4' },
    { name: 'Platform Core', items: ['Workflow Engine', 'Rules Engine', 'Audit Trail', 'E-signatures', 'Config Registry'], color: '#F5A623' },
    { name: 'Enterprise Systems', items: ['QMS/eDMS', 'ERP', 'MES', 'LIMS', 'Historian'], color: '#4C6EF5' },
  ];

  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 16,
      padding: 22,
    }}>
      {layers.map((layer, i) => (
        <div key={layer.name}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '140px 1fr',
            gap: 14,
            alignItems: 'stretch',
          }} className="details-architecture-row">
            <div style={{
              border: `1px solid ${layer.color}35`,
              background: `${layer.color}12`,
              borderRadius: 12,
              padding: '16px 14px',
              color: layer.color,
              fontSize: 13,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}>{layer.name}</div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: 10,
            }}>
              {layer.items.map(item => (
                <div key={item} style={{
                  minHeight: 52,
                  borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.07)',
                  background: 'rgba(13,17,23,0.65)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '10px 12px',
                  textAlign: 'center',
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: 1.35,
                }}>{item}</div>
              ))}
            </div>
          </div>
          {i < layers.length - 1 && (
            <div style={{
              width: 1,
              height: 24,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(0,201,177,0.45), rgba(255,255,255,0.08))',
              margin: '8px auto',
            }} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function ProductDetails({ onBookDemo }) {
  const [ref, inView] = useInView();
  const datasheetHref = `${process.env.PUBLIC_URL}/assets/qualicore-data-sheet.pdf`;

  return (
    <section id="resources" style={{
      padding: '120px 24px',
      background: 'linear-gradient(180deg, rgba(13,17,23,0.95), rgba(22,27,36,0.72), rgba(13,17,23,0.95))',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div ref={ref} style={{
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}>
          <SectionTitle
            eyebrow="Product Detail Pack"
            title="The missing buying details, in one place."
            body="Download the data sheet, inspect the technical fit, review the rollout model, understand the architecture, scan a case study, and compare pricing packages."
          />
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 0.95fr) minmax(0, 1.05fr)',
          gap: 22,
          alignItems: 'stretch',
          marginBottom: 86,
        }} className="details-two-col">
          <div style={{
            background: 'linear-gradient(135deg, rgba(0,201,177,0.11), rgba(123,63,228,0.09))',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 18,
            padding: 30,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: 340,
          }}>
            <div>
              <p style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.1em',
                color: '#00C9B1',
                textTransform: 'uppercase',
                marginBottom: 14,
                fontFamily: "'JetBrains Mono', monospace",
              }}>Downloadable Data Sheet</p>
              <h3 style={{ fontSize: 30, fontWeight: 900, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 14 }}>
                Qualicore GMP batch-release platform
              </h3>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.58)', lineHeight: 1.7, marginBottom: 22 }}>
                A concise PDF for QA, IT, validation, and manufacturing leaders evaluating review-by-exception batch release.
              </p>
              <ul style={{ listStyle: 'none', display: 'grid', gap: 10, marginBottom: 26 }}>
                {datasheetHighlights.map(item => (
                  <li key={item} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ color: '#00C9B1', fontWeight: 900, flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.68)', lineHeight: 1.5 }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <a href={datasheetHref} download style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'fit-content',
              padding: '13px 22px',
              borderRadius: 10,
              background: 'linear-gradient(135deg, #00C9B1, #7B3FE4)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 800,
              boxShadow: '0 4px 24px rgba(0,201,177,0.24)',
            }}>Download PDF</a>
          </div>

          <div id="specifications" style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 18,
            padding: 30,
          }}>
            <p style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.1em',
              color: '#7B3FE4',
              textTransform: 'uppercase',
              marginBottom: 14,
              fontFamily: "'JetBrains Mono', monospace",
            }}>Technical Specification</p>
            <h3 style={{ fontSize: 26, fontWeight: 900, color: '#fff', letterSpacing: '-0.02em', marginBottom: 18 }}>
              Built for regulated plant systems.
            </h3>
            <div style={{ display: 'grid', gap: 10 }}>
              {specs.map(spec => (
                <div key={spec.label} style={{
                  display: 'grid',
                  gridTemplateColumns: '130px 1fr',
                  gap: 14,
                  padding: '13px 0',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                }} className="details-spec-row">
                  <span style={{
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.36)',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>{spec.label}</span>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.72)', lineHeight: 1.55 }}>{spec.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div id="implementation" style={{ marginBottom: 86 }}>
          <SectionTitle
            eyebrow="Implementation"
            title="Pilot in weeks, then scale by site."
            body="Qualicore ships as a configured platform, with validation evidence and integration work planned around your existing SOPs."
            color="#F5A623"
          />
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            gap: 16,
          }}>
            {implementationSteps.map((step, i) => (
              <div key={step.title} style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 16,
                padding: 24,
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: ['#00C9B1', '#7B3FE4', '#F5A623', '#4C6EF5'][i],
                }} />
                <span style={{
                  display: 'inline-block',
                  color: ['#00C9B1', '#7B3FE4', '#F5A623', '#4C6EF5'][i],
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontFamily: "'JetBrains Mono', monospace",
                  marginBottom: 12,
                }}>{step.week}</span>
                <h3 style={{ fontSize: 17, fontWeight: 800, color: '#fff', marginBottom: 10 }}>{step.title}</h3>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.48)', lineHeight: 1.65 }}>{step.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div id="architecture" style={{ marginBottom: 86 }}>
          <SectionTitle
            eyebrow="Architecture Diagram"
            title="Server-authoritative, integration-first architecture."
            body="Devices render governed workflows; all authority stays in the platform core, with controlled connectors into enterprise systems."
            color="#4C6EF5"
          />
          <ArchitectureDiagram />
        </div>

        <div id="case-study" style={{ marginBottom: 86 }}>
          <SectionTitle
            eyebrow="Case Study"
            title="Sterile injectables release review pilot."
            body="A representative GMP manufacturing site used Qualicore to digitize a high-volume batch review process and reduce manual QA effort."
            color="#00C9B1"
          />
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)',
            gap: 20,
          }} className="details-two-col">
            <div style={{
              background: 'rgba(0,201,177,0.06)',
              border: '1px solid rgba(0,201,177,0.16)',
              borderRadius: 16,
              padding: 28,
            }}>
              <p style={{ fontSize: 12, color: '#00C9B1', fontWeight: 800, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Outcome Snapshot
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {[
                  ['68%', 'manual review effort reduced'],
                  ['10 weeks', 'pilot to controlled rollout'],
                  ['0', 'missed e-signature gates'],
                  ['3.2x', 'reviewer throughput uplift'],
                ].map(([value, label]) => (
                  <div key={label} style={{
                    borderTop: '1px solid rgba(255,255,255,0.12)',
                    paddingTop: 14,
                  }}>
                    <div style={{ fontSize: 34, fontWeight: 900, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1 }}>{value}</div>
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.48)', lineHeight: 1.45, marginTop: 8 }}>{label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 16,
              padding: 28,
            }}>
              <div style={{ display: 'grid', gap: 20 }}>
                {[
                  ['Challenge', 'QA reviewed every page of every BPR manually, while deviations, missing entries, and late lab results created release bottlenecks.'],
                  ['Solution', 'Qualicore configured exception rules, e-sign gates, Veeva document references, ERP disposition handoff, and AI-assisted legacy BPR intake.'],
                  ['Result', 'Reviewers focused on exceptions, supervisors closed completeness gaps earlier, and release disposition moved from end-stage chasing to controlled workflow.'],
                ].map(([label, text]) => (
                  <div key={label}>
                    <h3 style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: 7 }}>{label}</h3>
                    <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div id="pricing">
          <SectionTitle
            eyebrow="Pricing"
            title="Modular pricing, licensed by quality domain."
            body="Qualicore Platform is licensed per module, so teams pay only for the quality domains they run. Every module ships with up to 50 named users; combine any set of modules under a single Qualicore Platform subscription."
            color="#7B3FE4"
          />

          <div style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 18,
            padding: '8px 8px 0',
            marginBottom: 20,
            overflowX: 'auto',
          }}>
            <div style={{ minWidth: 680 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(220px, 2.1fr) 130px 110px 100px 150px',
                gap: 12,
                padding: '16px 22px',
                position: 'sticky',
                top: 0,
              }} className="pricing-table-row">
                {['Module', 'Price (INR/mo)', 'Price (USD/mo)', 'Users', ''].map((h, i) => (
                  <span key={h || i} style={{
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.38)',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>{h}</span>
                ))}
              </div>
              {modulePricing.map((row, i) => (
                <div key={row.module} style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(220px, 2.1fr) 130px 110px 100px 150px',
                  gap: 12,
                  alignItems: 'center',
                  padding: '16px 22px',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                  background: i % 2 === 1 ? 'rgba(255,255,255,0.015)' : 'transparent',
                }} className="pricing-table-row">
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#fff', lineHeight: 1.4 }}>{row.module}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>{row.inr}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#00C9B1' }}>{row.usd}</span>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>50</span>
                  <button
                    onClick={onBookDemo}
                    style={{
                      justifySelf: 'start',
                      padding: '8px 16px',
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#fff',
                      background: 'rgba(123,63,228,0.14)',
                      border: '1px solid rgba(123,63,228,0.4)',
                      borderRadius: 8,
                      cursor: 'pointer',
                      transition: 'background 0.2s, border-color 0.2s',
                      fontFamily: 'Inter, sans-serif',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(123,63,228,0.28)'; e.currentTarget.style.borderColor = 'rgba(123,63,228,0.7)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(123,63,228,0.14)'; e.currentTarget.style.borderColor = 'rgba(123,63,228,0.4)'; }}
                  >Contact Us</button>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 14,
            marginBottom: 20,
          }}>
            {[
              ['Per module, per month', 'Prices shown are indicative monthly rates per module, billed per site.'],
              ['Up to 50 users included', 'Every module license covers up to 50 named users out of the box.'],
              ['Bundle & save', 'Combine 4+ modules on one Qualicore Platform subscription for volume pricing.'],
            ].map(([title, body]) => (
              <div key={title} style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 14,
                padding: '18px 20px',
              }}>
                <p style={{ fontSize: 13, fontWeight: 800, color: '#fff', marginBottom: 6 }}>{title}</p>
                <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>{body}</p>
              </div>
            ))}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 16,
            background: 'linear-gradient(135deg, rgba(0,201,177,0.1), rgba(123,63,228,0.1))',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 16,
            padding: '22px 26px',
          }}>
            <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, maxWidth: 640 }}>
              Final pricing depends on number of sites, module mix, connectors, and validation scope. Talk to us for an enterprise quote tailored to your rollout.
            </p>
            <button onClick={onBookDemo} style={{
              flexShrink: 0,
              padding: '13px 26px',
              fontSize: 14,
              fontWeight: 800,
              color: '#fff',
              background: 'linear-gradient(135deg, #00C9B1, #7B3FE4)',
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              boxShadow: '0 4px 24px rgba(0,201,177,0.24)',
              fontFamily: 'Inter, sans-serif',
            }}>Contact Sales →</button>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 860px) {
          .details-two-col { grid-template-columns: 1fr !important; }
          .details-architecture-row { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 560px) {
          .details-spec-row { grid-template-columns: 1fr !important; gap: 6px !important; }
        }
        @media (max-width: 640px) {
          .pricing-table-row { grid-template-columns: minmax(180px, 2fr) 100px 90px 70px 130px !important; }
        }
      `}</style>
    </section>
  );
}
